import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { PendingRegistration, Company, Subscriber } from '../types';

export const useAdmin = () => {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const prevIsAdminRef = useRef<boolean | undefined>(undefined);
  
  // Reset queries only when isAdmin changes from true to false
  useEffect(() => {
    if (prevIsAdminRef.current === true && !isAdmin) {
      queryClient.resetQueries({ queryKey: ['subscribers'] });
      queryClient.resetQueries({ queryKey: ['pending_registrations'] });
      queryClient.resetQueries({ queryKey: ['companies'] });
    }
    prevIsAdminRef.current = isAdmin;
  }, [isAdmin, queryClient]);
  
  // Subscribers query - only run for admins
  const subscribersQuery = useQuery<Subscriber[]>({
    queryKey: ['subscribers'],
    queryFn: async ({ signal }) => {
      // Check if query was aborted
      if (signal?.aborted) {
        throw new DOMException('Query was aborted', 'AbortError');
      }
      
      try {
        const { data, error } = await supabase
          .from('subscribers')
          .select('*')
          .order('created_at', { ascending: false });
        
        // Check again after request (in case it was aborted during request)
        if (signal?.aborted) {
          throw new DOMException('Query was aborted', 'AbortError');
        }

        if (error) {
          // Better error message for RLS issues
          if (error.code === '42501' || error.message.includes('RLS') || error.message.includes('policy') || error.message.includes('permission')) {
            throw new Error(`RLS Policy Error: ${error.message}. Ensure you are authenticated as an admin with @thesimpleintel.com email.`);
          }
          throw error;
        }
        
        return data || [];
      } catch (err) {
        // Don't treat abort errors as failures
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        // Re-throw other errors
        throw err;
      }
    },
    enabled: !!isAdmin,
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Fetch pending registrations
  const pendingRegistrationsQuery = useQuery({
    queryKey: ['pending_registrations'],
    queryFn: async ({ signal }) => {
      // Check if query was aborted
      if (signal?.aborted) {
        throw new DOMException('Query was aborted', 'AbortError');
      }
      
      try {
        const { data, error } = await supabase
          .from('pending_registrations')
          .select('*')
          .eq('status', 'pending')
          .order('created_at', { ascending: false });
        
        // Check again after request
        if (signal?.aborted) {
          throw new DOMException('Query was aborted', 'AbortError');
        }

        if (error) {
          if (error.code === '42501' || error.message.includes('RLS') || error.message.includes('policy') || error.message.includes('permission')) {
            throw new Error(`RLS Policy Error: ${error.message}. Ensure you are authenticated as an admin with @thesimpleintel.com email.`);
          }
          throw error;
        }
        
        return (data || []) as PendingRegistration[];
      } catch (err) {
        // Don't treat abort errors as failures
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        throw err;
      }
    },
    enabled: isAdmin,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Fetch all companies
  const companiesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: async ({ signal }) => {
      // Check if query was aborted
      if (signal?.aborted) {
        throw new DOMException('Query was aborted', 'AbortError');
      }
      
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('*')
          .order('created_at', { ascending: false });
        
        // Check again after request
        if (signal?.aborted) {
          throw new DOMException('Query was aborted', 'AbortError');
        }

        if (error) {
          if (error.code === '42501' || error.message.includes('RLS') || error.message.includes('policy') || error.message.includes('permission')) {
            throw new Error(`RLS Policy Error: ${error.message}. Ensure you are authenticated as an admin with @thesimpleintel.com email.`);
          }
          throw error;
        }
        
        return (data || []) as Company[];
      } catch (err) {
        // Don't treat abort errors as failures
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw err;
        }
        throw err;
      }
    },
    enabled: isAdmin,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const subscribeMutation = useMutation({
    mutationFn: async (email: string) => {
      // Get user's company_id from user_companies
      let companyId: string | null = null
      if (user?.id) {
        const { data: userCompany } = await supabase
          .from('user_companies')
          .select('company_id')
          .eq('user_id', user.id)
          .single()
        
        companyId = userCompany?.company_id || null
      }

      const { error } = await supabase
        .from('subscribers')
        .insert({ 
          email, 
          user_id: user?.id || null,
          company_id: companyId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
    },
  });

  // DEPRECATED: approveRegistrationMutation - replaced by createUserAccount Edge Function
  // The old SQL function approve_registration expected users to exist in auth.users,
  // but the new flow creates users via Edge Function. This is kept for backward compatibility
  // but should not be used in new code.
  // const approveRegistrationMutation = useMutation({
  //   mutationFn: async ({ registrationId, companyId }: { registrationId: string; companyId: string }) => {
  //     const { data, error } = await supabase.rpc('approve_registration', {
  //       registration_id: registrationId,
  //       target_company_id: companyId,
  //       approver_user_id: user?.id || null,
  //     });
  //     if (error) throw error;
  //     if (data?.error) throw new Error(data.error);
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ['pending_registrations'] });
  //     queryClient.invalidateQueries({ queryKey: ['user_companies'] });
  //     queryClient.invalidateQueries({ queryKey: ['companies'] });
  //   },
  // });

  const rejectRegistrationMutation = useMutation({
    mutationFn: async (registrationId: string) => {
      // Use database function to handle rejection
      const { data, error } = await supabase.rpc('reject_registration', {
        registration_id: registrationId,
        approver_user_id: user?.id || null,
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending_registrations'] });
    },
  });

  const createCompanyMutation = useMutation({
    mutationFn: async ({ name, domain }: { name: string; domain: string }) => {
      const { data, error } = await supabase
        .from('companies')
        .insert({
          name,
          domain,
          status: 'active',
        })
        .select()
        .single();

      if (error) throw error;
      return data as Company;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  // Create user account with temporary password
  const createUserAccountMutation = useMutation({
    mutationFn: async ({ 
      email, 
      name, 
      companyId, 
      registrationId,
      designation,
      designation_other,
    }: { 
      email: string
      name: string
      companyId: string
      registrationId: string
      designation?: string | null
      designation_other?: string | null
    }) => {
      // Get current session and pass explicitly (fixes 401 when gateway JWT verification fails)
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Session expired. Please sign out and sign in again to create accounts.')
      }

      const { data: functionData, error: functionError } = await supabase.functions.invoke(
        'create-user-account',
        {
          body: {
            email,
            name,
            companyId,
            registrationId,
            designation: designation ?? null,
            designation_other: designation_other ?? null,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      )

      // Prefer server error message from response body (e.g. 500 returns { error: "..." })
      const serverMsg = typeof functionData?.error === 'string' ? functionData.error : null
      const msg = serverMsg ?? functionError?.message ?? String(functionError ?? '')

      if (functionError || serverMsg) {
        if (/401|Unauthorized/i.test(msg)) {
          throw new Error(
            'Session may have expired (401). Please sign out and sign in again, then try Create Account.'
          )
        }
        if (/Database error creating new user|trigger|duplicate|unique/i.test(msg)) {
          throw new Error(
            `Create Account failed: ${msg}. Ensure migrations are applied (run: npx supabase db push).`
          )
        }
        if (/Failed to send|Edge Function|fetch|network/i.test(msg)) {
          throw new Error(
            'Create Account failed: the Edge Function may not be deployed or reachable. ' +
            'Run: supabase functions deploy create-user-account (and ensure the project is linked and Edge Function secrets are set in the Dashboard).'
          )
        }
        throw new Error(msg || 'Create Account failed')
      }
      
      return functionData as { success: boolean; userId: string; tempPassword: string }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending_registrations'] })
      queryClient.invalidateQueries({ queryKey: ['user_companies'] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  return {
    isAdmin,
    subscribers: subscribersQuery.data || [],
    subscribersLoading: subscribersQuery.isLoading,
    subscribersError: subscribersQuery.error instanceof DOMException && subscribersQuery.error.name === 'AbortError' ? null : subscribersQuery.error,
    refetchSubscribers: subscribersQuery.refetch,
    subscribe: subscribeMutation.mutate,
    subscribeLoading: subscribeMutation.isPending,
    pendingRegistrations: pendingRegistrationsQuery.data || [],
    pendingRegistrationsLoading: pendingRegistrationsQuery.isLoading,
    pendingRegistrationsError: pendingRegistrationsQuery.error instanceof DOMException && pendingRegistrationsQuery.error.name === 'AbortError' ? null : pendingRegistrationsQuery.error,
    refetchPendingRegistrations: pendingRegistrationsQuery.refetch,
    rejectRegistration: rejectRegistrationMutation.mutate,
    rejectRegistrationLoading: rejectRegistrationMutation.isPending,
    companies: companiesQuery.data || [],
    companiesLoading: companiesQuery.isLoading,
    createCompany: createCompanyMutation.mutate,
    createCompanyLoading: createCompanyMutation.isPending,
    createUserAccount: createUserAccountMutation.mutate,
    createUserAccountAsync: createUserAccountMutation.mutateAsync,
    createUserAccountLoading: createUserAccountMutation.isPending,
  };
};
